import {useGeocoder} from "./useGeocoder";
import {useEffect, useMemo, useState} from "react";
import {getQueryParamsFromPlace} from "../../util/AddressUtils";

type GeocoderResult = google.maps.GeocoderResult;

export const useGeocodeByAddress = ({address, region}: {address: string | undefined, region?: string}) => {
    const { loading: geocoderLoading, geocoder } = useGeocoder();
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [geocodedAddress, setGeocodedAddress] = useState<GeocoderResult>();
    const [geocodingFailed, setGeocodingFailed] = useState<Error | boolean>();

    const geocodingLoading = useMemo(() => {
        return geocoderLoading || isLoading || !geocoder?.geocode
    }, [geocoder, geocoderLoading, isLoading])

    useEffect(() => {
        let isCancelled = false;

        if (address && geocoder?.geocode && !geocoderLoading) {
            setIsLoading(true);
            setGeocodingFailed(false);
            // If a user navigates to this page via URL only
            geocoder.geocode({ address, region }).then(({ results: places }: google.maps.GeocoderResponse) => {
                if (isCancelled) return;
                if (places && places.length > 0) {
                    const place = places[0];
                    // Validate that geocoded result contains required city and state components
                    const addressParts = getQueryParamsFromPlace(place);
                    if (addressParts?.city && addressParts?.state) {
                        setGeocodedAddress({...place});
                    } else {
                        setGeocodingFailed(new Error('Geocoded address missing city or state'));
                    }
                } else {
                    setGeocodingFailed(new Error('No geocode results'));
                    setIsLoading(false);
                }
            }).catch((error: any) => {
                if (!isCancelled) setGeocodingFailed(error)
            }).finally(() => {
                if (!isCancelled) setIsLoading(false);
            });
        } else {
            setIsLoading(false);
        }

        return () => { isCancelled = true; };
    }, [address, region, geocoder, geocoderLoading]);

    return {
        geocodedAddress,
        geocodingFailed,
        geocodingLoading
    }
}
